import { useMemo, useState, useEffect, useCallback } from "react";
import api from "../services/api";
import { useTasks } from "../context/TasksContext";

export default function TaskList() {
  const { refreshTrigger } = useTasks();
  
  // --- فیلترهای اصلی ---
  const [filter, setFilter] = useState("today"); // today | incomplete | all
  const [selectedProject, setSelectedProject] = useState("all"); // فیلتر پروژه
  const [selectedActivity, setSelectedActivity] = useState("all"); // فیلتر نوع فعالیت

  const [allTasks, setAllTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  // --- State های صفحه‌بندی و مودال ---
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;

  const [selectedTask, setSelectedTask] = useState(null);
  const [modalDescription, setModalDescription] = useState("");

  // --- 1. دریافت اطلاعات ---
  const fetchTasks = useCallback(async () => {
    try {
      const res = await api.get(`work-logs/?_nc=${Date.now()}`);
      setAllTasks(res.data);
    } catch (error) {
      console.error("Error fetching tasks:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks, refreshTrigger]);

  // ریست کردن صفحه وقتی هر کدام از فیلترها عوض میشه
  useEffect(() => {
    setCurrentPage(1);
  }, [filter, selectedProject, selectedActivity]);

  // --- 2. استخراج لیست پروژه‌ها و فعالیت‌ها برای دراپ‌داون ---
  const uniqueProjects = useMemo(() => {
    const projects = allTasks
      .map(t => t.project_title)
      .filter(p => p !== null && p !== undefined && p !== "");
    return [...new Set(projects)];
  }, [allTasks]);

  const uniqueActivities = useMemo(() => {
    const activities = allTasks
      .map(t => t.task_type_title)
      .filter(a => a !== null && a !== undefined && a !== "");
    return [...new Set(activities)];
  }, [allTasks]);

  // --- 3. توابع کمکی ---
  const toPersianDigits = (str) => {
    if (!str) return str;
    return str.toString().replace(/\d/g, d => "۰۱۲۳۴۵۶۷۸۹"[d]);
  };

  const formatDate = (isoString) => {
    if (!isoString) return "-";
    return new Date(isoString).toLocaleDateString("fa-IR", {
      year: "numeric", month: "2-digit", day: "2-digit",
    });
  };

  const formatTime = (isoString) => {
    if (!isoString) return "-- : --";
    const t = new Date(isoString).toLocaleTimeString("fa-IR", {
      hour: "2-digit", minute: "2-digit",
    });
    return toPersianDigits(t);
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return "-";
    return `${formatDate(isoString)} / ${formatTime(isoString)}`;
  };

  // --- تابع ۱: محاسبه زنده برای کارهای در حال انجام (با ثانیه) ---
  const calculateDurationLive = (start, end, pauseSeconds = 0) => {
    if (!start) return "-- : --";
    const endTime = end ? new Date(end) : new Date();
    const startTime = new Date(start);
    let diffMs = endTime - startTime;
    
    if (pauseSeconds) {
        diffMs = diffMs - (pauseSeconds * 1000);
    }
    
    if (diffMs < 0) return toPersianDigits("00 : 00");
    
    const hours = Math.floor(diffMs / 3600000);
    const minutes = Math.floor((diffMs % 3600000) / 60000);
    return toPersianDigits(`${String(hours).padStart(2, '0')} : ${String(minutes).padStart(2, '0')}`);
  };

  // --- تابع ۲: تبدیل دقیقه‌ی ذخیره شده در دیتابیس به ساعت:دقیقه ---
  const formatFinalDuration = (minutes) => {
      const h = Math.floor(minutes / 60);
      const m = minutes % 60;
      return toPersianDigits(`${String(h).padStart(2, '0')} : ${String(m).padStart(2, '0')}`);
  };

  const renderEfficiency = (task) => {
    if (task.status !== 'done' || !task.estimated_minutes) return null;
    const eff = task.efficiency;
    const isPositive = eff >= 0;
    
    return (
      <span 
        style={{
            fontSize: '0.85rem',
            marginRight: '8px',
            color: isPositive ? '#10b981' : '#9ca3af',
            fontWeight: isPositive ? 'bold' : 'normal',
            direction: 'rtl'
        }}
      >
        {isPositive ? `(+ ${toPersianDigits(eff)} دقیقه)` : `(${toPersianDigits(eff)} دقیقه)`}
      </span>
    );
  };

  const getStatusInfo = (status) => {
    switch (status) {
      case "done": return { text: "انجام شد", mark: "✓", color: "#10b981" };
      case "doing": return { text: "در حال انجام", mark: "...", color: "#3b82f6" };
      case "suspended": return { text: "متوقف شده", mark: "||", color: "#f59e0b" }; // تغییر متن و آیکون
      case "pending": return { text: "در انتظار", mark: "!", color: "#6b7280" };
      default: return { text: status, mark: "?", color: "#868686" };
    }
  };

  const nowText = useMemo(() => {
    try {
      const d = new Date();
      const dateFormatter = new Intl.DateTimeFormat("fa-IR-u-ca-persian-nu-latn", {
        weekday: "long", year: "numeric", month: "long", day: "numeric",
      });
      const parts = dateFormatter.formatToParts(d);
      const get = (type) => parts.find(p => p.type === type)?.value;
      const time = d.toLocaleTimeString("fa-IR-u-nu-latn", { hour: "2-digit", minute: "2-digit" });
      return `امروز ${get("weekday")} ${get("day")} ${get("month")} ${get("year")} ساعت ${time}`;
    } catch { return "امروز"; }
  }, []);

  // --- 4. اعمال فیلترها ---
  const filteredTasks = useMemo(() => {
    let tasks = [...allTasks];
    tasks.sort((a, b) => new Date(b.start_time) - new Date(a.start_time));
    
    // 1. فیلتر زمانی/وضعیت
    if (filter === "today") {
      // استفاده از formatDate برای یکسان شدن دقیق فرمت‌ها
      const today = formatDate(new Date().toISOString());
      tasks = tasks.filter(t => formatDate(t.start_time) === today);
    } else if (filter === "incomplete") {
      tasks = tasks.filter(t => t.status !== 'done');
    }

    // 2. فیلتر پروژه
    if (selectedProject !== "all") {
        tasks = tasks.filter(t => t.project_title === selectedProject);
    }

    // 3. فیلتر نوع فعالیت
    if (selectedActivity !== "all") {
        tasks = tasks.filter(t => t.task_type_title === selectedActivity);
    }
    
    return tasks;
  }, [filter, allTasks, selectedProject, selectedActivity]);

  const shownTasks = useMemo(() => {
    // اگر فیلتر "همه" نباشد، صفحه‌بندی نیاز نیست (طبق کد قبلی شما)
    // اما چون الان فیلترهای ترکیبی داریم، بهتر است همیشه صفحه‌بندی اعمال شود یا شرط را نگه داریم
    // اینجا شرط قبلی شما را حفظ کردم:
    if (filter === "today" || filter === "incomplete") return filteredTasks;
    
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredTasks.slice(startIndex, startIndex + itemsPerPage);
  }, [filter, filteredTasks, currentPage]);

  const totalPages = Math.ceil(filteredTasks.length / itemsPerPage);

  // --- 5. هندلرهای مودال ---
  const handleOpenModal = (task) => {
    setSelectedTask(task);
    setModalDescription(task.description || "");
  };

  const handleCloseModal = () => {
    setSelectedTask(null);
    setModalDescription("");
  };

  const handleAction = async (actionType) => {
    if (!selectedTask) return;
    try {
        if (actionType === 'delete') {
            if(!window.confirm("آیا از حذف این کار مطمئن هستید؟")) return;
            await api.delete(`work-logs/${selectedTask.id}/`);
        } 
        else if (actionType === 'start') {
            await api.post(`work-logs/${selectedTask.id}/start_task/`);
        }
        
        // --- تغییر جدید برای Resume ---
        else if (actionType === 'resume') {
            // فراخوانی اندپوینت اختصاصی که در ویو ساختیم
            await api.post(`work-logs/${selectedTask.id}/resume_task/`);
        } 
        // -----------------------------
        
        else if (actionType === 'finish') {
            await api.post(`work-logs/${selectedTask.id}/finish_task/`, { status: 'done' });
        }
        else if (actionType === 'suspend') {
            await api.post(`work-logs/${selectedTask.id}/finish_task/`, { status: 'suspended' });
        }
        else if (actionType === 'update') {
            await api.patch(`work-logs/${selectedTask.id}/`, { description: modalDescription });
            alert("توضیحات بروزرسانی شد");
        }

        fetchTasks();
        if (actionType !== 'update') handleCloseModal();
    } catch (error) {
        console.error(error);
        if (error.response && error.response.data) {
             alert("خطا: " + (error.response.data.detail || JSON.stringify(error.response.data)));
        } else {
             alert("خطا در انجام عملیات");
        }
    }
  };

  const canSuspendTask = true;


  const handleQuickAction = async (task, actionType) => {
    try {
      if (actionType === 'start') {
        await api.post(`work-logs/${task.id}/start_task/`);
      } else if (actionType === 'finish') {
        await api.post(`work-logs/${task.id}/finish_task/`, { status: 'done' });
      } else if (actionType === 'resume') {
        await api.post(`work-logs/${task.id}/resume_task/`);
      }
      fetchTasks();
    } catch (error) {
      console.error("Error in quick action:", error);
      alert("خطا در انجام عملیات");
    }
  };


  return (
      <>
      <div className="container">
        <div className="row p-full">
          <div className="col-12">
            <div className="task-list">
              
              <div className="task-list-header">
                <section>
                  <h1>لیست کارها</h1>
                  
                  {/* --- دکمه‌های فیلتر اصلی --- */}
                  <div>
                    <button type="button" className={filter === "today" ? "active" : ""} onClick={() => setFilter("today")}>کارهای امروز</button>
                    <button type="button" className={filter === "incomplete" ? "active" : ""} onClick={() => setFilter("incomplete")}>ناتمام</button>
                    <button type="button" className={filter === "all" ? "active" : ""} onClick={() => setFilter("all")}>همه</button>
                  </div>

                </section>
                <p><svg><use xlinkHref="#reservIcon" /></svg>{nowText}</p>
              </div>


              {/* --- دراپ‌داون‌های فیلتر --- */}
              <div className="dropdown-wrapper">
                  <select 
                    value={selectedProject} 
                    onChange={(e) => setSelectedProject(e.target.value)}
                  >
                      <option value="all">همه پروژه‌ها</option>
                      {uniqueProjects.map((p, index) => (
                          <option key={index} value={p}>{p}</option>
                      ))}
                  </select>

                  <select 
                    value={selectedActivity} 
                    onChange={(e) => setSelectedActivity(e.target.value)}
                  >
                      <option value="all">همه فعالیت‌ها</option>
                      {uniqueActivities.map((a, index) => (
                          <option key={index} value={a}>{a}</option>
                      ))}
                  </select>
              </div>

              <div className="task-list-wrapper">
                <section className="task-list-row-header">
                  <div>عنوان</div>
                  <div>کاربر</div>
                  <div>تاریخ</div>
                  <div>شروع</div>
                  <div>پایان</div>
                  <div>زمان انجام (بهره‌وری)</div>
                  <div>وضعیت</div>
                  <div>عملیات</div>
                  <div>تنطیمات</div>
                </section>

                {loading ? (
                    <div style={{padding:'20px', textAlign:'center'}}>در حال دریافت...</div>
                ) : shownTasks.length > 0 ? (
                  shownTasks.map((t) => {
                    const statusInfo = getStatusInfo(t.status);
                    const fullTitle = t.project_title ? `${t.project_title} / ${t.title}` : t.title;
                    const userName = t.user_full_name || t.username || "-"; 
                    const dateStr = formatDate(t.start_time);
                    const startStr = t.status === 'pending' ? "-- : --" : formatDateTime(t.start_time);
                    const endStr = t.end_time ? formatDateTime(t.end_time) : "-- : --";
                    let durationStr;
                    if (t.status === 'pending') {
                        durationStr = "-- : --"; 
                    } else if (t.status === 'done' || t.status === 'suspended') {
                        durationStr = formatFinalDuration(t.final_time || 0);
                    } else {
                        durationStr = calculateDurationLive(t.start_time, t.end_time, t.pause_duration);
                    }

                    return (
                      <section key={t.id} className="task-list-row-item">
                        <div aria-label="عنوان" aria-details={fullTitle}>
                          <span>{fullTitle}</span>
                        </div>
                        <div aria-label="کاربر" aria-details={userName}>
                          <span>{userName}</span> 
                        </div>
                        <div aria-label="تاریخ" aria-details={dateStr}>
                          <span>{dateStr}</span>
                        </div>
                        <div aria-label="شروع" aria-details={startStr}>
                          <span>{startStr}</span>
                        </div>
                        <div aria-label="پایان" aria-details={endStr}>
                          <span>{endStr}</span>
                        </div>
                        <div aria-label="زمان انجام" aria-details={durationStr}>
                          <span>{durationStr}</span>
                          {renderEfficiency(t)}
                        </div>
                        <div aria-label="وضعیت">
                          <span style={{color: statusInfo.color}}>{statusInfo.text}</span>
                          <bdi style={{color: statusInfo.color}}>{statusInfo.mark}</bdi>
                        </div>
                        <div>
                          {t.status === 'pending' && (
                            <button className="task-btn" onClick={() => handleQuickAction(t, 'start')}>
                              <span>شروع</span>
                              <svg><use xlinkHref="#b26965"></use></svg>
                            </button>
                          )}
                          {t.status === 'doing' && (
                            <button className="task-btn" onClick={() => handleQuickAction(t, 'finish')}>
                              <span>پایان</span>
                              <svg><use xlinkHref="#bbf3a9" /></svg>
                              </button>
                          )}
                          {t.status === 'suspended' && (
                            <button className="task-btn" onClick={() => handleQuickAction(t, 'resume')}>
                              <span>ادامه</span>
                              <svg><use xlinkHref="#b26965"></use></svg>
                            </button>
                          )}
                          {t.status === 'done' && (
                            <span style={{ fontSize: '0.85rem', color: '#10b981' }}>تکمیل شده</span>
                          )}
                        </div>
                        <div>
                          <button type="button" onClick={() => handleOpenModal(t)}>بیشتر</button>
                        </div>
                      </section>
                    );
                  })
                ) : (
                  <div style={{textAlign:'center', color:'var(--txt-clr)'}}>
                    {filter === 'today' ? 'امروز هنوز کاری ثبت نکرده‌اید.' : 
                     filter === 'incomplete' ? 'کار ناتمامی ندارید.' : 'هیچ کاری با این مشخصات یافت نشد.'}
                  </div>
                )}
              </div>

              {/* --- Pagination --- */}
              {filter === 'all' && totalPages > 1 && (
                  <div className="paginatioon-wrapper">
                      <button type="button" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>قبلی</button>
                      <span>صفحه <bdi>{toPersianDigits(currentPage)}</bdi> از <ins>{toPersianDigits(totalPages)}</ins></span>
                      <button type="button" disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>بعدی</button>
                  </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* --- Task Detail Modal (همان کدهای قبلی) --- */}
      {selectedTask && (
        <div className="edit-modal-overlay" onClick={(e) => { if(e.target === e.currentTarget) handleCloseModal(); }}>
            <div className="edit-modal-content">
                <div className="edit-modal-header">
                    <h3>جزئیات کار</h3>
                    <button onClick={handleCloseModal} className="close-modal-btn">&times;</button>
                </div>
                <div className="edit-modal-body">
                    <div className="modal-box">
                      <div>
                          <bdi>عنوان:</bdi>
                          <span>{selectedTask.project_title ? `${selectedTask.project_title} / ${selectedTask.title}` : selectedTask.title}</span>
                      </div>
                      <div>
                          <bdi>کاربر:</bdi>
                          <span>{selectedTask.user_full_name || selectedTask.username || "کاربر"}</span>
                      </div>
                      <div>
                          <bdi>نوع فعالیت:</bdi>
                          <span>
                              {selectedTask.task_type_title || "سایر"}
                              {selectedTask.estimated_minutes > 0 ? ` (${toPersianDigits(selectedTask.estimated_minutes)} دقیقه)` : ""}
                          </span>
                      </div>
                      <div>
                          <bdi>تاریخ ثبت:</bdi>
                          <span>{formatDate(selectedTask.created_date || selectedTask.start_time)}</span>
                      </div>
                      {selectedTask.status !== 'pending' && (
                          <div>
                              <div>
                                  <bdi>شروع: </bdi> 
                                  {formatTime(selectedTask.start_time)}
                              </div>
                              {selectedTask.end_time && (
                                  <>
                                    <div>
                                        <bdi>پایان: </bdi> 
                                        {formatTime(selectedTask.end_time)}
                                    </div>
                                    <div>
                                        <bdi>بهره‌وری: </bdi>
                                        {renderEfficiency(selectedTask) || "-"}
                                    </div>
                                  </>
                              )}
                              {(selectedTask.status === 'done' || selectedTask.status === 'suspended') && (
                                  <div>
                                      <bdi>زمان نهایی: </bdi> 
                                      {formatFinalDuration(selectedTask.final_time || 0)}
                                  </div>
                              )}
                          </div>
                      )}
                      <div>
                          <label><strong>توضیحات:</strong></label>
                          <textarea 
                              className="modal-input" 
                              rows="4" 
                              value={modalDescription}
                              onChange={(e) => setModalDescription(e.target.value)}
                              placeholder="توضیحات کار..."
                          />
                      </div>
                    </div>
                </div>
                <div className="edit-modal-footer">
                    <div className="modal-box">
                      <button className="save-btn btn-1" onClick={() => handleAction('update')}>بروزرسانی توضیحات</button>
                      {selectedTask.status === 'pending' && (
                          <>
                              <button className="save-btn btn-2" onClick={() => handleAction('start')}>شروع کار</button>
                              <button className="save-btn btn-3" onClick={() => handleAction('delete')}>حذف کار</button>
                          </>
                      )}
                      {selectedTask.status === 'doing' && (
                          <>
                              <button 
                                  className="save-btn btn-3" 
                                  onClick={() => handleAction('suspend')}
                                  disabled={!canSuspendTask}
                                  style={{ opacity: !canSuspendTask ? 0.5 : 1, cursor: !canSuspendTask ? 'not-allowed' : 'pointer' }}
                              >
                                  توقف
                              </button>
                              <button className="save-btn btn-5" onClick={() => handleAction('finish')}>پایان کار</button>
                          </>
                      )}
                      {selectedTask.status === 'suspended' && (
                          <>
                              <button className="save-btn btn-2" onClick={() => handleAction('resume')}>ادامه کار</button>
                              <button className="save-btn btn-4" onClick={() => handleAction('finish')}>پایان کار</button>
                          </>
                      )}
                    </div>
                </div>
            </div>
        </div>
      )}
      </>
  );
}